import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../../api/client'
import { setProjectTestFlag } from '../../../api/projects'
import type { Project } from '../../../api/types'
import { useAuth } from '../../../context/AuthContext'
import { isSuperAdminUserId } from '../../../lib/superAdminIds'
import TestProjectFlagRow from '../../projects/modal/TestProjectFlagRow'

export interface AdminCompanyRow {
  id: number
  name: string
  seat_limit: number
  seats_used: number
  plan_type: string | null
  subscription_status: string | null
}

export default function AdminPanel() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<number | null>(null)
  const [seatDraft, setSeatDraft] = useState<Record<number, string>>({})
  const [seatBusy, setSeatBusy] = useState<number | null>(null)
  const [seatErr, setSeatErr] = useState<string | null>(null)

  const isAdmin = !!user?.is_admin
  const isSuperAdmin = isSuperAdminUserId(user?.id)

  const { data: projectRows } = useQuery({
    queryKey: ['projects', 'admin-include-test'],
    queryFn: () => apiFetch<Project[]>('GET', '/api/projects?include_test=1'),
    enabled: isAdmin,
  })

  const { data: companiesRes, isLoading: companiesLoading } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => apiFetch<{ data: AdminCompanyRow[] }>('GET', '/api/admin/companies'),
    enabled: isAdmin,
  })

  const companies = companiesRes?.data ?? []
  const projects = Array.isArray(projectRows) ? projectRows : []

  if (!isAdmin) return null

  async function saveSeatLimit(companyId: number) {
    const raw = seatDraft[companyId] ?? String(companies.find((c) => c.id === companyId)?.seat_limit ?? '')
    const seat_limit = parseInt(raw, 10)
    if (!Number.isFinite(seat_limit) || seat_limit < 1) {
      setSeatErr('Seat limit must be at least 1')
      return
    }
    setSeatBusy(companyId)
    setSeatErr(null)
    try {
      await apiFetch('PATCH', `/api/admin/companies/${companyId}/seat-limit`, { seat_limit })
      await qc.invalidateQueries({ queryKey: ['admin-companies'] })
    } catch (e) {
      setSeatErr(e instanceof Error ? e.message : 'Failed to update seat limit')
    } finally {
      setSeatBusy(null)
    }
  }

  return (
    <section data-testid="settings-admin-panel">
      <h2 className="set-panel-title">Admin</h2>
      {isSuperAdmin && (
        <p className="set-panel-lead">
          <Link to="/admin/customers" data-testid="admin-customers-link">Customers & billing →</Link>
          {' · '}
          <Link to="/admin/help-gaps" data-testid="admin-help-gaps-link">Help gaps →</Link>
          {' · '}
          <Link to="/admin/agent-activity" data-testid="admin-agent-activity-link">Agent activity →</Link>
        </p>
      )}

      <h3 className="set-panel-subtitle" style={{ marginTop: 20 }}>Company seats</h3>
      <p className="set-panel-lead">
        Seat limit edits are audited on the server (who, when, old → new).
      </p>
      {seatErr && <p className="set-err" data-testid="admin-seat-error">{seatErr}</p>}
      {companiesLoading && <p>Loading companies…</p>}
      <table className="set-admin-companies" data-testid="admin-companies-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Plan</th>
            <th>Used</th>
            <th>Limit</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => (
            <tr key={c.id} data-testid={`admin-company-row-${c.id}`}>
              <td>{c.name || `Company ${c.id}`}</td>
              <td>{c.plan_type || '—'} · {c.subscription_status || '—'}</td>
              <td data-testid={`admin-company-used-${c.id}`}>{c.seats_used}</td>
              <td>
                <input
                  type="number"
                  min={1}
                  max={500}
                  className="fld-i"
                  style={{ width: 72 }}
                  data-testid={`admin-company-seat-input-${c.id}`}
                  value={seatDraft[c.id] ?? String(c.seat_limit)}
                  onChange={(e) => setSeatDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn b-ol btn-sm"
                  data-testid={`admin-company-seat-save-${c.id}`}
                  disabled={seatBusy === c.id}
                  onClick={() => saveSeatLimit(c.id)}
                >
                  {seatBusy === c.id ? 'Saving…' : 'Save'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="set-panel-subtitle" style={{ marginTop: 28 }}>Test projects</h3>
      <p className="set-panel-lead">
        Test projects stay hidden from billing sync. Existing values are unchanged.
      </p>
      <ul className="set-admin-projects">
        {projects.map((p) => (
          <li key={p.id} data-testid={`admin-test-flag-${p.id}`}>
            <div className="set-admin-project-name">{p.name || `Project ${p.id}`}</div>
            <TestProjectFlagRow
              visible
              checked={!!p.is_test_project}
              disabled={busyId === p.id}
              onChange={async (checked) => {
                setBusyId(p.id)
                try {
                  await setProjectTestFlag(p.id, checked)
                  await qc.invalidateQueries({ queryKey: ['projects'] })
                } finally {
                  setBusyId(null)
                }
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}
